import { useState } from "react";
import { ArrowLeft } from "lucide-react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { ColleagueSearch } from "@/components/messages/ColleagueSearch";
import { MessageComposer } from "@/components/messages/MessageComposer";
import { RoomsForm } from "@/components/messages/RoomsForm";
import { useCity } from "@/contexts/CityContext";
import { track } from "@/lib/analytics";
import {
  useCreateMessageConversation,
  type MessageDirectoryMember,
} from "@/lib/messages";
import "@/styles/messages.css";

export default function MessagesNew() {
  const { city } = useCity();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const facilityId = searchParams.get("facility")?.trim() ?? "";
  const requestedHandle = searchParams.get("handle")?.trim() ?? "";
  const requestedUserId = searchParams.get("to")?.trim() ?? "";
  const [recipient, setRecipient] = useState<MessageDirectoryMember | null>(null);
  const createConversation = useCreateMessageConversation(city.slug);

  return (
    <section className="pb-messages-new" aria-labelledby="messages-new-heading">
      <Link className="pb-messages-back" to="/board/messages">
        <ArrowLeft size={13} aria-hidden="true" />Back to Messages
      </Link>
      <header>
        <p className="pb-board-eyebrow">{facilityId ? "Email a facility" : "Private direct message"}</p>
        <h1 id="messages-new-heading">{facilityId ? "Message the rooms" : "New message"}</h1>
        <p>
          {facilityId
            ? "Send a non-patient-specific note to the practice’s listed contact."
            : `Choose a message-enabled colleague in ${city.name}, then start the conversation.`}
        </p>
      </header>

      {facilityId ? (
        <RoomsForm citySlug={city.slug} facilityId={facilityId} />
      ) : (
        <div className="pb-messages-new-direct">
          <ColleagueSearch
            citySlug={city.slug}
            selected={recipient}
            requestedHandle={requestedHandle}
            requestedUserId={requestedUserId}
            onSelect={setRecipient}
          />
          {recipient ? (
            <MessageComposer
              autoFocus
              label={`Message ${recipient.display}`}
              submitLabel="Start conversation"
              onSend={async (body) => {
                const response = await createConversation.mutateAsync({
                  recipientUserId: recipient.userId,
                  body,
                });
                track("conversation_create", {
                  conversation_id: response.conversation.id,
                  recipient_user_id: recipient.userId,
                });
                track("message_send", {
                  conversation_id: response.conversation.id,
                  message_id: response.message.id,
                });
                navigate(`/board/messages/${encodeURIComponent(response.conversation.id)}`);
              }}
            />
          ) : null}
        </div>
      )}
    </section>
  );
}
